import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { updateError } from './user';
import { dispatch } from '../store';
import { getPracticeId } from './clincheck';
import { updateCurrentTemplateFavourite } from './whatsappV2';
import { isQuickReplyTemplate } from '../../components/whatsappComponents/quickReplyUtils';

// Personal Reply templates (usage_type QUICK_REPLY, or the quick_reply_ name
// prefix as a fallback) power the closed-window composer only. They must never
// surface in the normal WhatsApp template pickers / admin lists, so we strip
// them from the global list every source feeds. The composer reads its templates
// via its own path, so hiding them here doesn't affect it.
const excludeQuickReplyTemplates = (templates) =>
    (Array.isArray(templates) ? templates : []).filter((t) => !isQuickReplyTemplate(t));

const initialState = {
    loadingForWpTemplate: false,
    wpTemplateListGlobal: null,
    showWpSubjectListGlobal: false,
};

const slice = createSlice({
    name: 'whatsappMessages',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        setLoadingForWpTemplate(state, action) {
            state.loadingForWpTemplate = action.payload;
        },
        setWpTemplateListGlobal(state, action) {
            state.wpTemplateListGlobal = action.payload;
        },
        updateShowWpSubjectList(state, action) {
            state.showWpSubjectListGlobal = action.payload;
        },
        updateTemplateUsageType(state, action) {
            const { templateId, usageType } = action.payload;
            if (Array.isArray(state.wpTemplateListGlobal)) {
                state.wpTemplateListGlobal = state.wpTemplateListGlobal.map((t) =>
                    t.id === templateId ? { ...t, usage_type: usageType } : t
                );
            }
        },
        removeTemplateById(state, action) {
            if (Array.isArray(state.wpTemplateListGlobal)) {
                state.wpTemplateListGlobal = state.wpTemplateListGlobal.filter(t => t.id !== action.payload);
            }
        },
        bulkUpdateUsageType(state, action) {
            const { ids, usageType } = action.payload;
            if (Array.isArray(state.wpTemplateListGlobal)) {
                state.wpTemplateListGlobal = state.wpTemplateListGlobal.map(t =>
                    ids.includes(t.id) ? { ...t, usage_type: usageType } : t
                );
            }
        },
        revertUsageTypes(state, action) {
            const oldValues = action.payload; // { [id]: usageType }
            if (Array.isArray(state.wpTemplateListGlobal)) {
                state.wpTemplateListGlobal = state.wpTemplateListGlobal.map(t =>
                    oldValues[t.id] !== undefined ? { ...t, usage_type: oldValues[t.id] } : t
                );
            }
        },
        updateSingleUsageType(state, action) {
            const { id, usageType } = action.payload;
            if (Array.isArray(state.wpTemplateListGlobal)) {
                const idx = state.wpTemplateListGlobal.findIndex(t => t.id === id);
                if (idx !== -1) state.wpTemplateListGlobal[idx] = { ...state.wpTemplateListGlobal[idx], usage_type: usageType };
            }
        },
        // Optimistic favourite toggle — flips is_favourite on the template
        // matching `id`. Called before the PATCH; reverted on failure.
        updateTemplateFavourite(state, action) {
            const { id, isFavourite } = action.payload;
            if (Array.isArray(state.wpTemplateListGlobal)) {
                const idx = state.wpTemplateListGlobal.findIndex(t => t.id === id);
                if (idx !== -1) state.wpTemplateListGlobal[idx] = { ...state.wpTemplateListGlobal[idx], is_favourite: isFavourite };
            }
        },
    }
});

export default slice.reducer;

export const { reset, updateShowWpSubjectList, setWpTemplateListGlobal, updateTemplateUsageType, setLoadingForWpTemplate, removeTemplateById, bulkUpdateUsageType, revertUsageTypes, updateSingleUsageType, updateTemplateFavourite } = slice.actions;

export const handleLoading = (state) => {
    if (state === false) {
        setTimeout(() => {
            dispatch(slice.actions.setLoadingForWpTemplate(state));
        }, 500);
    } else {
        dispatch(slice.actions.setLoadingForWpTemplate(state));
    }
};

const getQuery = (data) => {
    const params = [];
    if (data.search) {
        params.push(`query=${data.search}`);
    }
    if (data.usageTypeFilter?.length > 0) {
        params.push(`usage_type__in=${data.usageTypeFilter.join(',')}`);
    }
    return params.length > 0 ? `?${params.join('&')}` : '';
};

export function getSearchWpTemplateList(data) {
    const { wpAccountId } = data;
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return;
        // Guard: skip API call if business account PK is missing
        if (!wpAccountId) return;
        dispatch(slice.actions.updateShowWpSubjectList(true));
        try {
            handleLoading(true);
            const practiceId = getPracticeId();
            const response = await callApi(`/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/${getQuery(data)}`);
            if (response.status === 200) {
                dispatch(slice.actions.setWpTemplateListGlobal(excludeQuickReplyTemplates(response.data.results)));
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false, status: response?.status }));
            }
            handleLoading(false);
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Something went wrong', success: false, cause: error instanceof Error ? new Error(error.message) : new Error(String(error?.message ?? 'error')) }));
            handleLoading(false);
        }
    };
}

export function updateWpContextMap(payload) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return;
        try {
            const { wpAccountId, context, id } = payload ?? {};
            // Guard: skip API call if business account PK or template id is missing
            if (!wpAccountId || !id) return;
            const practiceId = getPracticeId();
            const reqBody = {
                template_context_mapping: context
            };
            const response = await callApi(`/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/${id}/`, 'PATCH', reqBody);
            if (response.status === 200) {
                dispatch(getSearchWpTemplateList({ wpAccountId }));
            }
        } catch (err) {
            console.error(err);
        }
    };
}

export function get10WpTemplateList({ wpAccountId, usageTypeFilter }) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return;
        // Guard: skip API call if business account PK is missing
        if (!wpAccountId) return;
        const practiceId = getPracticeId();
        dispatch(slice.actions.updateShowWpSubjectList(true));
        try {
            handleLoading(true);
            const query = getQuery({ usageTypeFilter });
            const response = await callApi(`/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/${query}`);
            handleLoading(false);
            if (response.status === 200) {
                dispatch(slice.actions.setWpTemplateListGlobal(excludeQuickReplyTemplates(response.data.results)));
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false, status: response?.status }));
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Something went wrong', success: false, cause: error instanceof Error ? new Error(error.message) : new Error(String(error?.message ?? 'error')) }));
            handleLoading(false);
        }
    };
}

/**
 * Optimistic favourite toggle. PATCHes is_favourite on the BE; reverts
 * the local optimistic update and shows an error toast on failure.
 *
 * @param {object} opts
 * @param {string|number} opts.practiceId
 * @param {string|number} opts.wpAccountId
 * @param {string|number} opts.templateId
 * @param {boolean}       opts.newFavourite  - the desired state (after toggle)
 */
export function toggleTemplateFavourite({ practiceId, wpAccountId, templateId, newFavourite }) {
    return async () => {
        if (!wpAccountId || !templateId) return;
        // Optimistic update
        dispatch(slice.actions.updateTemplateFavourite({ id: templateId, isFavourite: newFavourite }));
        dispatch(updateCurrentTemplateFavourite({ id: templateId, isFavourite: newFavourite }));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/${templateId}/`,
                'PATCH',
                { is_favourite: newFavourite }
            );
            if (response.status !== 200) {
                // Revert on non-200
                dispatch(slice.actions.updateTemplateFavourite({ id: templateId, isFavourite: !newFavourite }));
                dispatch(updateCurrentTemplateFavourite({ id: templateId, isFavourite: !newFavourite }));
                dispatch(updateError({ message: 'Failed to update favourite — please try again.', success: false }));
            }
        } catch (err) {
            // Revert on network error
            dispatch(slice.actions.updateTemplateFavourite({ id: templateId, isFavourite: !newFavourite }));
            dispatch(updateCurrentTemplateFavourite({ id: templateId, isFavourite: !newFavourite }));
            dispatch(updateError({ message: err?.message || 'Failed to update favourite.', success: false }));
        }
    };
}
